import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as R from 'ramda'
import { Utils } from '@pbt/pbt-ui-components'

import type { Employee, ProblemEntry } from '~/api/graphql/generated/types'
import { useProblemsWidgetSubscription } from '~/api/graphql/subscriptions/problems'
import {
  getDiagnosisProblemStates,
  getDiagnosisStatus,
  getFindingProblemStates,
} from '~/store/reducers/constants'
import {
  type DiagnosisSnapshotItem,
  DiagnosisSnapshotItemType,
  type MasterProblemsSnapshotItem,
  ProblemStates,
} from '~/types'

import { useSubscriptionRetry } from './useSubscriptionRetry'

interface UseNormalizedProblemsSubscriptionReturn {
  AddedByAiDiagnosisStatusId: string
  RemovedDiagnosisStatusId: string
  RemovedFindingStatusId: string
  ResolvedDiagnosisStatusId: string
  ResolvedFindingStatusId: string
  RuledOutDiagnosisStatusId: string
  activeProblems: MasterProblemsSnapshotItem[]
  error: Error | null
  historicProblems: MasterProblemsSnapshotItem[]
  isResolved: (diagnosis: MasterProblemsSnapshotItem) => boolean
  loading: boolean
  problemCount: number
}

/**
 * Custom hook that fetches problems data via GraphQL subscription and returns
 * normalized/transformed data ready for component consumption.
 *
 * This hook encapsulates:
 * - GraphQL subscription to problems data
 * - Data transformation from GraphQL union types to MasterProblemsSnapshotItem
 * - Data partitioning into active vs historic problems
 * - Loading and error state management with retry logic
 * - Business logic for filtering and grouping problems
 */
export const useNormalizedProblemsSubscription = (
  patientId: string,
): UseNormalizedProblemsSubscriptionReturn => {
  const {
    data: subscriptionData,
    loading,
    error,
    restart,
  } = useProblemsWidgetSubscription(patientId)

  const problemsData = subscriptionData?.problemsWidget

  const DiagnosisStatus = useSelector(getDiagnosisProblemStates)
  const NonProblemDiagnosisStatus = useSelector(getDiagnosisStatus)
  const FindingStatus = useSelector(getFindingProblemStates)

  const statusIds = useMemo(
    () => ({
      ResolvedDiagnosisStatusId: Utils.findConstantIdByName(
        ProblemStates.RESOLVED,
        DiagnosisStatus,
      ),
      RemovedDiagnosisStatusId: Utils.findConstantIdByName(
        ProblemStates.REMOVED,
        DiagnosisStatus,
      ),
      ResolvedFindingStatusId: Utils.findConstantIdByName(
        ProblemStates.RESOLVED,
        FindingStatus,
      ),
      RemovedFindingStatusId: Utils.findConstantIdByName(
        ProblemStates.REMOVED,
        FindingStatus,
      ),
      RuledOutDiagnosisStatusId: Utils.findConstantIdByName(
        ProblemStates.RULED_OUT,
        DiagnosisStatus,
      ),
      AddedByAiDiagnosisStatusId: Utils.findConstantIdByName(
        ProblemStates.ADDED_BY_AI,
        NonProblemDiagnosisStatus,
      ),
    }),
    [DiagnosisStatus, NonProblemDiagnosisStatus, FindingStatus],
  )

  const createVetUser = (vet: Employee) => ({
    id: vet.id || '',
    firstName: vet.firstName || '',
    lastName: vet.lastName || '',
    email: vet.email || '',
    active: true,
    deleted: vet.deleted ?? false,
    mobilePhone: vet.mobilePhone || '',
    photo: vet.photo || '',
    photoThumbnail: vet.photoThumbnail || '',
  })

  const createBaseSnapshot = (
    entry: ProblemEntry,
  ): Partial<DiagnosisSnapshotItem> => ({
    id: entry.id || '',
    date: entry.date || null,
    businessId: entry.business?.id ?? null,
    locationId: entry.business?.id || '',
    statusId: ('status' in entry && entry.status?.id) || undefined,
    vet: createVetUser(
      'vet' in entry ? (entry.vet ?? ({} as Employee)) : ({} as Employee),
    ),
    // Default values for required string fields that may not exist on all types
    diagnosedBy: '',
    diagnosedIn: '',
    diagnosisId: '',
    descriptionTranslation: '',
    soapId: '',
  })

  // Transform GraphQL union types to DiagnosisSnapshotItem
  const transformProblemEntry = (entry: ProblemEntry) => {
    switch (entry.__typename) {
      case 'SnapshotProblemLog':
        return {
          ...createBaseSnapshot(entry),
          name: entry.description || '',
          notes: entry.notes || '',
          description: entry.description || '',
          descriptionTranslation: entry.descriptionTranslation || '',
          soapId: entry.soap?.id || '',
          type:
            entry.type === 'PROBLEM_DIAGNOSIS_LOG'
              ? DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG
              : entry.type === 'PROBLEM_FINDING_LOG'
                ? DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG
                : DiagnosisSnapshotItemType.DIAGNOSIS_LOG,
          problemId: entry.problemId,
          enums: entry.enums?.map((enumItem) => ({
            id: enumItem.id || '',
            problemLogId: enumItem.problemLogId || '',
            enumId: enumItem.enumId || '',
            enumValueId: enumItem.enumValueId || '',
            parentEnumValueId: enumItem.parentEnumValueId,
            notes: enumItem.notes || '',
            modificationDate:
              enumItem.modificationDate || new Date().toISOString(),
            creationDate: enumItem.creationDate || new Date().toISOString(),
            creatorId: enumItem.creatorId || '',
            modifierId: enumItem.modifierId || '',
          })),
        }

      case 'SnapshotDiagnosisHistory':
        return {
          ...createBaseSnapshot(entry),
          name: entry.description || '',
          notes: entry.notes || '',
          diagnosedBy: entry.diagnosedBy || '',
          diagnosedIn: entry.diagnosedIn || '',
          diagnosisId: entry.diagnosisId || '',
          description: entry.description || '',
          descriptionTranslation: entry.descriptionTranslation || '',
          type: DiagnosisSnapshotItemType.DIAGNOSIS_HISTORY,
        }

      case 'SnapshotMedicalHistory':
        return {
          ...createBaseSnapshot(entry),
          name: entry.name || '',
          notes: entry.details || '',
          diagnosedIn: entry.business?.name || '',
          description: entry.name || '',
          vet: null,
        }

      default:
        return {
          ...createBaseSnapshot({} as any),
          name: '',
          notes: '',
          description: '',
          type: undefined,
        }
    }
  }

  const normalizedProblems = useMemo(() => {
    if (!problemsData?.problems) return []
    return problemsData.problems.map((entry) => transformProblemEntry(entry))
  }, [problemsData])

  // Group problems by problemId and create MasterProblemsSnapshotItem
  const groupedProblems: MasterProblemsSnapshotItem[] = useMemo(
    () =>
      R.pipe(
        // Filter out AI-added diagnosis history items
        R.filter(
          (cur: DiagnosisSnapshotItem) =>
            !(
              cur.type === DiagnosisSnapshotItemType.DIAGNOSIS_HISTORY &&
              cur.statusId === statusIds.AddedByAiDiagnosisStatusId
            ),
        ),
        // Group by problemId (use id as fallback for non-problem items)
        R.groupBy(
          (diagnosis: DiagnosisSnapshotItem) =>
            diagnosis.problemId || diagnosis.id,
        ),
        R.values,
        // Sort each group by date (newest first)
        R.map(R.sort(R.descend(R.prop('date')))),
        // Create master items with children
        R.map((problemGroup): MasterProblemsSnapshotItem => {
          const head = R.head(problemGroup)
          if (!head) {
            throw new Error('Problem group is empty')
          }
          return {
            ...head,
            children: problemGroup,
          }
        }),
      )(normalizedProblems),
    [normalizedProblems, statusIds.AddedByAiDiagnosisStatusId],
  )

  // Business logic functions
  const isResolved = (diagnosis: MasterProblemsSnapshotItem) =>
    (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG &&
      diagnosis.statusId === statusIds.ResolvedDiagnosisStatusId) ||
    (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG &&
      diagnosis.statusId === statusIds.ResolvedFindingStatusId)

  const isNotRemoved = (diagnosis: MasterProblemsSnapshotItem) =>
    !(
      (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG &&
        (diagnosis.statusId === statusIds.RemovedDiagnosisStatusId ||
          diagnosis.statusId === statusIds.RuledOutDiagnosisStatusId)) ||
      (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG &&
        diagnosis.statusId === statusIds.RemovedFindingStatusId)
    )

  // Partition problems into active and historic
  const [historicProblems, activeProblems] = useMemo(
    () => R.partition(isResolved, groupedProblems.filter(isNotRemoved)),
    [groupedProblems, statusIds],
  )

  useSubscriptionRetry({
    error,
    restart,
    subscriptionName: 'Problems subscription',
  })

  return {
    activeProblems,
    historicProblems,
    loading,
    error: error || null,
    isResolved,
    problemCount: problemsData?.problemsCounters?.problemCount || 0,
    ...statusIds,
  }
}
